import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  followUser,
  unfollowUser,
  isFollowing,
} from '@/services/follow.service';
import { useToast } from '@/hooks/use-toast';
import { logButtonClick, logError } from '@/services/analytics.service';
import { FollowButtonComponent } from './FollowButton.component';

interface FollowButtonProps {
  userId: string;
  username: string;
  onFollowChange?: () => void;
}

export const FollowButton: React.FC<FollowButtonProps> = ({
  userId,
  username,
  onFollowChange,
}) => {
  const { user: currentUser } = useAuth();
  const { toast } = useToast();
  const [isFollowingUser, setIsFollowingUser] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [initialLoading, setInitialLoading] = useState<boolean>(true);

  // Check follow status on mount
  useEffect(() => {
    const checkFollowStatus = async () => {
      if (!currentUser || currentUser.uid === userId) {
        setInitialLoading(false);
        return;
      }

      try {
        const following = await isFollowing(currentUser.uid, userId);
        setIsFollowingUser(following);
      } catch (error) {
        console.error('Error checking follow status:', error);
      } finally {
        setInitialLoading(false);
      }
    };

    checkFollowStatus();
  }, [currentUser, userId]);

  const handleFollow = async () => {
    if (!currentUser) {
      toast({
        title: 'Please sign in',
        description: 'You need to sign in to follow users.',
        variant: 'destructive',
      });
      return;
    }

    if (currentUser.uid === userId) {
      return; // Should not happen, but safety check
    }

    setLoading(true);
    logButtonClick(isFollowingUser ? 'Unfollow' : 'Follow', 'FollowButton');

    try {
      if (isFollowingUser) {
        await unfollowUser(currentUser.uid, userId);
        setIsFollowingUser(false);
        toast({
          title: 'Unfollowed',
          description: `You unfollowed ${username}`,
        });
      } else {
        await followUser(currentUser.uid, userId);
        setIsFollowingUser(true);
        toast({
          title: 'Following',
          description: `You are now following ${username}`,
        });
      }

      // Notify parent component of change
      if (onFollowChange) {
        onFollowChange();
      }
    } catch (error) {
      console.error('Error toggling follow:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to update follow status';
      logError('Follow Error', errorMessage, 'FollowButton', {
        userId,
        username,
      });
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <FollowButtonComponent
      currentUser={currentUser}
      userId={userId}
      username={username}
      isFollowingUser={isFollowingUser}
      loading={loading}
      initialLoading={initialLoading}
      onFollow={handleFollow}
    />
  );
};

